/**
 * Cart-drawer "Combine with" auto-recommendations.
 *
 * Reads each `[data-upsell-recommendations]` container in the
 * cart drawer (one per variant — `inline` for mobile, `side-panel`
 * for desktop), fetches Shopify product recommendations for every
 * product in the cart via `/recommendations/products?...&section_id=api-cart-recommendations`,
 * and appends de-duplicated cards into the matching live
 * `<ul data-cart-slider-track>`, tagging each appended `<li>` with
 * `data-recommendation-added` so they can be cleared on cart change.
 *
 * Triggered on DOMContentLoaded and on every cart-mutating fetch
 * (`/cart/add.js`, `/cart/change.js`, `/cart/update.js`,
 *  `/cart/clear.js`) via a fetch wrap. Also listens for the theme's
 * own `theme:cart:change` event as a secondary trigger.
 *
 * Adapts the reference implementation by reading product ids from
 * the slider's `[data-product-id]` attribute on each `<li>`
 * (the theme's existing slider doesn't use `data-quick-add-holder`
 * as the canonical id source).
 */
(function () {
  'use strict';

  var RECOMMENDATIONS_URL =
    (window.theme && window.theme.routes && window.theme.routes.product_recommendations_url) ||
    '/recommendations/products';
  var SECTION_ID = 'api-cart-recommendations';
  var CART_MUTATING_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function getContainers() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-upsell-recommendations]')
    );
  }

  function readCartProductIds(container) {
    var raw = container.getAttribute('data-cart-product-ids') || '';
    return raw
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function readExistingSliderIds(track) {
    if (!track) return [];
    return Array.prototype.slice
      .call(track.querySelectorAll('[data-cart-slider-item][data-product-id]'))
      .map(function (li) { return li.getAttribute('data-product-id'); })
      .filter(Boolean);
  }

  function fetchRecommendationsForContainer(container) {
    var cartIds = readCartProductIds(container);
    if (!cartIds.length) return Promise.resolve();

    var variant = container.getAttribute('data-variant') || 'side-panel';
    var limit = parseInt(container.getAttribute('data-recommendations-limit'), 10);
    if (!limit || limit < 0) limit = 4;

    var track = container.querySelector('[data-cart-slider-track]');
    if (!track) return Promise.resolve();

    var seen = {};
    cartIds.forEach(function (id) { seen[id] = true; });
    readExistingSliderIds(track).forEach(function (id) { seen[id] = true; });

    var appended = 0;

    var requests = cartIds.map(function (id) {
      var url =
        RECOMMENDATIONS_URL +
        (RECOMMENDATIONS_URL.indexOf('?') === -1 ? '?' : '&') +
        'product_id=' + encodeURIComponent(id) +
        '&section_id=' + encodeURIComponent(SECTION_ID);
      return fetch(url, { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.text() : ''; })
        .catch(function () { return ''; });
    });

    return Promise.all(requests).then(function (htmls) {
      htmls.forEach(function (html) {
        if (!html || appended >= limit) return;
        var doc;
        try {
          doc = new DOMParser().parseFromString(html, 'text/html');
        } catch (e) {
          return;
        }
        var results = doc.querySelector('[data-results]');
        if (!results) return;
        var variantContainer = results.querySelector('[data-variant="' + variant + '"]');
        if (!variantContainer) return;
        var items = Array.prototype.slice.call(
          variantContainer.querySelectorAll('li[data-cart-slider-item]')
        );
        for (var i = 0; i < items.length && appended < limit; i++) {
          var li = items[i];
          var pid = li.getAttribute('data-product-id');
          if (!pid || seen[pid]) continue;
          seen[pid] = true;
          li.setAttribute('data-recommendation-added', '');
          track.appendChild(li);
          appended += 1;
        }
      });
    });
  }

  function cartUpsellRecommendations() {
    var containers = getContainers();
    if (!containers.length) return Promise.resolve();
    return Promise.all(containers.map(fetchRecommendationsForContainer));
  }

  function clearAppendedRecommendations() {
    var nodes = document.querySelectorAll('[data-recommendation-added]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.parentNode) node.parentNode.removeChild(node);
    });
  }

  function refreshCartProductIds() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        var seen = {};
        var ids = [];
        cart.items.forEach(function (item) {
          var pid = String(item.product_id);
          if (!seen[pid]) {
            seen[pid] = true;
            ids.push(pid);
          }
        });
        var joined = ids.join(',');
        getContainers().forEach(function (c) {
          c.setAttribute('data-cart-product-ids', joined);
        });
      })
      .catch(function () { /* network error — leave existing ids */ });
  }

  var refreshScheduled = false;
  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    // Defer to the next microtask so the cart-mutating response is
    // fully processed (theme cart UI updates first), then refresh.
    Promise.resolve().then(function () {
      refreshScheduled = false;
      refreshCartProductIds().then(function () {
        clearAppendedRecommendations();
        cartUpsellRecommendations();
      });
    });
  }

  // Wrap fetch so we react to any cart mutation regardless of which
  // theme component triggered it. The original response is passed
  // through untouched — we only schedule a refresh after success.
  if (typeof window.fetch === 'function' && !window.__cartUpsellFetchWrapped) {
    window.__cartUpsellFetchWrapped = true;
    var originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url =
        typeof input === 'string'
          ? input
          : (input && input.url) || '';
      var isCartMutation = CART_MUTATING_RE.test(url);
      var promise = originalFetch(input, init);
      if (isCartMutation) {
        promise.then(function (res) {
          if (res && res.ok) scheduleRefresh();
        }).catch(function () { /* ignored */ });
      }
      return promise;
    };
  }

  // Also react to the theme's own cart-change event if it dispatches
  // one (belt-and-suspenders — covers cases where cart mutates via
  // XHR rather than fetch, or via direct state updates).
  document.addEventListener('theme:cart:change', scheduleRefresh);
  document.addEventListener('cart:updated', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cartUpsellRecommendations);
  } else {
    cartUpsellRecommendations();
  }
})();
